import { zodResolver } from "@hookform/resolvers/zod";
import { RefreshCw } from "lucide-react";
import { useForm } from "react-hook-form";

import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Skeleton } from "components/ui/skeleton";

import {
  LOCATION_RADIUS_KM_MAX,
  LOCATION_RADIUS_KM_MIN,
  LOCATION_RADIUS_KM_STEP,
} from "../constants/gamification-settings";
import { GAMIFICATION_SETTINGS_COPY } from "../constants/messages";
import {
  type GamificationSettingsFormData,
  gamificationSettingsSchema,
} from "../validations/gamification-settings.validation";
import {
  useGamificationSettings,
  type UseGamificationSettingsResult,
} from "./use-gamification-settings";

const FIELD_ID = "locationRadiusKm";
const HELP_ID = "locationRadiusKm-help";
const ERROR_ID = "locationRadiusKm-error";

interface GamificationSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SettingsFormProps {
  initialRadiusKm: number | undefined;
  isSaving: boolean;
  onSave: (radiusKm: number) => Promise<boolean>;
  onClose: () => void;
}

function SettingsForm({
  initialRadiusKm,
  isSaving,
  onSave,
  onClose,
}: SettingsFormProps): JSX.Element {
  const { register, handleSubmit, formState } =
    useForm<GamificationSettingsFormData>({
      resolver: zodResolver(gamificationSettingsSchema),
      defaultValues: { locationRadiusKm: initialRadiusKm },
    });
  const errorMessage: string | undefined =
    formState.errors.locationRadiusKm?.message;
  const describedBy: string = errorMessage ? `${HELP_ID} ${ERROR_ID}` : HELP_ID;

  const submit = async (values: GamificationSettingsFormData): Promise<void> => {
    const saved: boolean = await onSave(values.locationRadiusKm);
    if (saved) onClose();
  };

  return (
    <form noValidate onSubmit={handleSubmit(submit)} className="flex flex-col gap-6">
      <div>
        <Label htmlFor={FIELD_ID}>{GAMIFICATION_SETTINGS_COPY.radiusLabel}</Label>
        <Input
          id={FIELD_ID}
          type="number"
          inputMode="numeric"
          min={LOCATION_RADIUS_KM_MIN}
          max={LOCATION_RADIUS_KM_MAX}
          step={LOCATION_RADIUS_KM_STEP}
          disabled={isSaving}
          hasError={Boolean(errorMessage)}
          aria-describedby={describedBy}
          aria-invalid={Boolean(errorMessage)}
          {...register("locationRadiusKm", { valueAsNumber: true })}
        />
        <p id={HELP_ID} className="mt-1 text-xs text-gray-500">
          {GAMIFICATION_SETTINGS_COPY.radiusHelp}
        </p>
        {errorMessage && (
          <p id={ERROR_ID} role="alert" className="mt-1 text-sm text-red-500">
            {errorMessage}
          </p>
        )}
      </div>
      <DialogFooter className="gap-2 sm:gap-0">
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          onClick={onClose}
          disabled={isSaving}
        >
          {GAMIFICATION_SETTINGS_COPY.cancel}
        </Button>
        <Button type="submit" className="min-h-11" disabled={isSaving}>
          {isSaving
            ? GAMIFICATION_SETTINGS_COPY.saving
            : GAMIFICATION_SETTINGS_COPY.save}
        </Button>
      </DialogFooter>
    </form>
  );
}

function SettingsBody({ onClose }: { onClose: () => void }): JSX.Element {
  const settings: UseGamificationSettingsResult = useGamificationSettings();

  if (settings.isLoading) {
    return (
      <div
        role="status"
        aria-label={GAMIFICATION_SETTINGS_COPY.loading}
        className="flex flex-col gap-3"
      >
        <Skeleton className="h-4 w-56" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-4 w-full" />
      </div>
    );
  }

  if (settings.isError) {
    return (
      <div role="alert" className="flex flex-col items-center gap-3 py-6 text-center">
        <p className="text-sm text-red-600">
          {GAMIFICATION_SETTINGS_COPY.loadError}
        </p>
        <Button
          type="button"
          variant="outline"
          className="min-h-11"
          onClick={settings.retry}
        >
          <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
          {GAMIFICATION_SETTINGS_COPY.retry}
        </Button>
      </div>
    );
  }

  return (
    <SettingsForm
      initialRadiusKm={settings.locationRadiusKm}
      isSaving={settings.isSaving}
      onSave={settings.saveLocationRadius}
      onClose={onClose}
    />
  );
}

export function GamificationSettingsDialog({
  open,
  onOpenChange,
}: GamificationSettingsDialogProps): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-md">
        <DialogHeader>
          <DialogTitle>{GAMIFICATION_SETTINGS_COPY.title}</DialogTitle>
          <DialogDescription>
            {GAMIFICATION_SETTINGS_COPY.description}
          </DialogDescription>
        </DialogHeader>
        <SettingsBody onClose={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  );
}
